import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";

import { WhitelabelSettingReadModel } from "../../../../../../../@api";

import { appSettingsKeys } from "../../../../../../models/app-setting-keys";
import { translationKeys } from "../../../../../../models/app-translation-keys";
import { applyCSSSetting } from "../../../../../../models/functions/apply-css-setting.function";
import { useWhitelabelService } from "../../../../../../services/ServiceHooks/whitelabelHook";
import { SettingList } from "../../Layout/SettingList/SettingList";
import { ICSSSettingListItem } from "../../Models/interfaces/css-setting-list-item.interface";
import { ISettingListItem } from "../../Models/interfaces/setting-list-item.interface";
import { SettingCheckboxInput } from "../../SettingLibrary/CheckboxInput/SettingCheckboxInput";

interface ILayoutSettingsProps {
  settings: WhitelabelSettingReadModel[];
  resetSetting: (key: string) => void;
}

export const LayoutSettings: FunctionComponent<ILayoutSettingsProps> = (props: ILayoutSettingsProps) => {
  const { t } = useTranslation();
  const whitelabelService = useWhitelabelService();

  const whitelabelTranslationKeys = translationKeys.adminArea.whiteLabel;
  const fontSettingTranslationsKeys = whitelabelTranslationKeys.areas.layout.settings;

  const settingItemTemplate = (
    setting: ICSSSettingListItem,
    whitelabelSetting: WhitelabelSettingReadModel | undefined,
  ) => {
    if (!whitelabelSetting) {
      return <div className="error">{t(whitelabelTranslationKeys.errors.settingNotFound)}</div>;
    }

    // Use effective value: customValue if set, otherwise defaultValue
    const effectiveValue =
      whitelabelSetting.customValue !== undefined && whitelabelSetting.customValue !== ""
        ? whitelabelSetting.customValue
        : whitelabelSetting.defaultValue;

    return (
      <SettingCheckboxInput
        value={booleanCheck(effectiveValue)}
        onChange={newValue => onBooleanValueChange(setting, newValue)}
      />
    );
  };

  const booleanCheck = (value: string | boolean) => {
    return value === "true" || (typeof value === "boolean" && value);
  };

  const settingList: ICSSSettingListItem[] = [
    {
      settingKey: appSettingsKeys["Layout.Border.HasRoundedBorder"],
      labelKey: fontSettingTranslationsKeys.borderRadius.label,
      cssKeys: ["border-radius", "border-radius-small"],
      cssPrefix: "customer-",
      hasReset: true,
      centerVertically: true,
      applyValueFunction: (setting: ICSSSettingListItem, value: any) =>
        setting.cssKeys.forEach(cssKey => applyCSSSetting(setting.cssPrefix ?? "", cssKey, value)),
      itemTemplate: settingItemTemplate,
      valueMapping: (value: boolean) => (value ? undefined : 0),
      updateFunction: whitelabelService.updateSetting.bind(whitelabelService),
    },
    {
      settingKey: appSettingsKeys["Layout.Positions.IsLogoLeft"],
      labelKey: fontSettingTranslationsKeys.logoPosition.label,
      cssKeys: ["header-row-direction"],
      cssPrefix: "customer-",
      hasReset: true,
      centerVertically: true,
      applyValueFunction: (setting: ICSSSettingListItem, value: any) =>
        applyCSSSetting(setting.cssPrefix ?? "", setting.cssKeys[0], value),
      itemTemplate: settingItemTemplate,
      valueMapping: (value: boolean) => (value ? "row" : "row-reverse"),
      updateFunction: whitelabelService.updateSetting.bind(whitelabelService),
    },
  ];

  const onBooleanValueChange = (setting: ICSSSettingListItem, newValue: boolean) => {
    const mappedValue = setting.valueMapping ? setting.valueMapping(newValue) : newValue;
    setting.applyValueFunction(setting, mappedValue);

    debounceBackendUpdateChange(setting, newValue ? "true" : "false");
  };

  const debounceBackendUpdateChange = debounce(
    (setting: ISettingListItem, value) => setting.updateFunction(setting.settingKey, value),
    300,
  );

  const resetSetting = async (setting: ICSSSettingListItem, wlSetting: WhitelabelSettingReadModel) => {
    const mappedValue = setting.valueMapping
      ? setting.valueMapping(wlSetting.defaultValue === "true")
      : wlSetting.defaultValue;
    setting.applyValueFunction(setting, mappedValue);

    props.resetSetting(setting.settingKey);
  };

  return <SettingList list={settingList} whiteLabelSettings={props.settings} resetSetting={resetSetting} />;
};
